import { MRT_RowSelectionState, MRT_Row } from "material-react-table";
import { useState, useEffect } from "react";
import { JobFlags, ImportTableColumn } from "../../../utilities/types";
import { toast } from "react-toastify";
import { API, Job, Contact } from "../../../api";
import { CREATE_NEW_SALESPERSON } from "../../../utilities/constants";
import { Oops } from "../../../utilities/oops";
import { FileDownloader } from "../../../utilities/downloadFile";
import { FeatureCollection, Point, Polygon, MultiPolygon } from "geojson";
import {
	validateJobs as validateJobsShared,
	FIELD_TO_PROPERTY_MAP,
	ValidationContext,
	parseValidationErrorKey,
	getValidationErrorKey,
} from "../../../utilities/jobValidation";
import { columnDisplayNames } from "../../admin/ImportSettings";
import { ImportTableViewModel, JobsImportTableProps, ValidationOptions } from "./ImportTable.types";
import { allJobsHaveNoFiles, findDeal, updateJobGroup, dealBasedSubRowUpdate } from "./ImportTable.helpers";
import { ColumnContext } from "./columns/ColumnHelpers";

export function useImportTableViewModel(props: JobsImportTableProps): ImportTableViewModel {
	// ============= STATE DECLARATIONS =============
	const [jobs, setJobs] = useState<Job[]>(props.jobs);
	const [readySelectionState, setReadySelectionState] = useState<Record<string, boolean>>({});
	const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
	// Track locally created salespersons that haven't been submitted yet
	const [newSalespersons, setNewSalespersons] = useState<Contact[]>([]);

	// ============= DROPDOWN OPTIONS STATE =============
	const [gridSizes] = useState<number[]>([
		...new Set([
			...props.allDeals.flatMap((deal) => {
				return deal.density;
			}),
			...props.jobs.flatMap((job) => (job.importDensity ? [job.importDensity] : [])),
		]),
	]);
	const [depths] = useState<number[]>([
		...new Set([
			...props.allDeals.flatMap((deal) => {
				return deal.depth;
			}),
			...props.jobs.flatMap((job) => (job.importDepth ? [job.importDepth] : [])),
		]),
	]);
	const [labPackages] = useState<string[]>([
		...new Set<string>([
			...props.allDeals.flatMap((deal) => {
				const field = deal.testPackage;
				return field !== undefined && field !== "" ? field : [];
			}),
			...props.jobs.flatMap((job) => (job.externalTestPackageId ? [job.externalTestPackageId] : [])),
			...["No test package specified"],
		]),
	]);
	const [micros] = useState<number[]>([
		...new Set(
			props.allDeals.flatMap((deal) => {
				return deal.frequencyOfMicros ?? 0;
			}),
		),
		...props.jobs.flatMap((job) => (job.externalFrequencyOfMicros ? [job.externalFrequencyOfMicros] : [])),
	]);

	// ============= VALIDATION CONTEXT =============
	const validationContext: ValidationContext = {
		company: props.selectedCompany,
		allDeals: props.allDeals,
		integration: props.integration,
	};

	// ============= HELPER FUNCTIONS =============
	// Helper function to get row-specific validation context (includes the deal for that row)
	const getRowValidationContext = (row: MRT_Row<Job>): ValidationContext => {
		const job = jobs.find((j) => j.id === row.original.id);
		const deal = props.allDeals?.find((d) => d.id === job?.dealIds?.[0]);
		const isCreatingNewSalesperson = job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON) ?? false;
		const hasPointsFile = true; // makes microsList visible; it will be disabled if no points file

		return {
			...validationContext,
			job,
			deal,
			isCreatingNewSalesperson,
			hasPointsFile,
		};
	};

	// Helper function to detect if a salesperson is locally created (has temp ID)
	const isNewSalesperson = (id: string): boolean => {
		return id.startsWith("temp-");
	};

	// Helper function to find salesperson data by ID from local list
	const findNewSalesperson = (id: string): Contact | undefined => {
		return newSalespersons.find((sp) => sp.id === id);
	};

	// ============= BUSINESS LOGIC FUNCTIONS =============
	const validateJobs = (
		jobsToValidate: Job[],
		options: ValidationOptions = { updateState: true, clearPrevious: true },
	): Record<string, string> => {
		const { updateState = true, clearPrevious = true } = options;

		// Use shared validation function
		const errors = validateJobsShared(jobsToValidate, validationContext);

		// Update state if requested
		if (updateState) {
			if (clearPrevious) {
				setValidationErrors(errors);
			} else {
				// Merge new errors and remove any that are no longer present for these jobs
				setValidationErrors((prev) => {
					const newErrors = { ...prev, ...errors };

					// For each job being validated, remove any previous errors that are no longer valid
					for (const job of jobsToValidate) {
						Object.entries(FIELD_TO_PROPERTY_MAP).forEach(([fieldType]) => {
							const errorKey = getValidationErrorKey(fieldType as ImportTableColumn, job.id);
							// If no new error was generated for this field, remove any existing error
							if (!errors[errorKey]) {
								delete newErrors[errorKey];
							}
						});
					}

					return newErrors;
				});
			}
		}

		return errors;
	};

	// Map-related functions (similar to JobsDashboardTable)
	function updateBoundaries(rowSelection: MRT_RowSelectionState) {
		if (!props.setDisplayBoundaries || !props.setDisplayedZones || !props.setDisplayedPoints) {
			console.warn("Map props not provided, skipping boundary update");
			return [0, 0, 0] as const;
		}

		try {
			const selectedJobs = Object.keys(rowSelection)
				.map((key) => {
					const index = parseInt(key);
					if (!isNaN(index) && rowSelection[key]) {
						return jobs[index];
					}
				})
				.filter(Boolean) as Job[];

			const displayedBoundaries: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>> = {};
			const displayedPoints: Record<string, FeatureCollection<Point>> = {};
			const displayedZones: Record<string, FeatureCollection<Polygon> | FeatureCollection<MultiPolygon>> = {};

			for (const job of selectedJobs) {
				if (!job) {
					continue;
				}

				const boundary = job.files.boundaryGeoJSON as
					| FeatureCollection<Polygon>
					| FeatureCollection<MultiPolygon>
					| undefined;
				const points = job.files.pointsGeoJSON as FeatureCollection<Point> | undefined;
				const zones = job.files.zonesGeoJSON as
					| FeatureCollection<Polygon>
					| FeatureCollection<MultiPolygon>
					| undefined;

				if (boundary) {
					displayedBoundaries[job.id] = boundary;
				}
				if (points) {
					displayedPoints[job.id] = points;
				}
				if (zones) {
					displayedZones[job.id] = zones;
				}
			}

			props.setDisplayBoundaries(displayedBoundaries);
			props.setDisplayedPoints(displayedPoints);
			props.setDisplayedZones(displayedZones);
			return [
				Object.keys(displayedBoundaries).length,
				Object.keys(displayedPoints).length,
				Object.keys(displayedZones).length,
			] as const;
		} catch (error) {
			Oops.handleError(error, "ImportTable.updateBoundaries");
			return [0, 0, 0] as const;
		}
	}

	// ============= EFFECTS =============
	useEffect(() => {
		// finds matching deals for jobs
		const updatedJobs = props.jobs.map((job) => {
			return {
				...job,
				deal: (
					findDeal(
						props.allDeals,
						job.importDensity ?? 0,
						job.importDepth ?? 0,
						job.externalTestPackageId ?? "",
						job.externalFrequencyOfMicros ?? 0,
					)?.id || "custom"
				).toString(),
			};
		});

		setJobs(updatedJobs);
		validateJobs(updatedJobs, { updateState: true, clearPrevious: true });
	}, [props.jobs, props.allDeals, props.selectedCompany]);

	// Notify parent component about validation state changes
	useEffect(() => {
		const hasValidationErrors = Object.keys(validationErrors).length > 0;
		if (props.onValidationChange) {
			props.onValidationChange(hasValidationErrors);
		}
	}, [validationErrors, props.onValidationChange]);

	// ============= COLUMN CONTEXT =============
	// Create context object with all dependencies that column builders need
	const columnContext: ColumnContext = {
		props,
		jobs,
		setJobs,
		validationErrors,
		validateJobs,
		validationContext,
		newSalespersons,
		setNewSalespersons,
		isNewSalesperson,
		findNewSalesperson,
		readySelectionState,
		setReadySelectionState,
		gridSizes,
		depths,
		labPackages,
		micros,
		getRowValidationContext,
		findDeal,
		updateJobGroup,
		dealBasedSubRowUpdate,
	};

	// ============= EXPORT/SUBMIT FUNCTIONS =============
	function hasValidationErrors() {
		return Object.keys(validationErrors).length > 0;
	}

	function getValidationErrorSummary(): { field: string; count: number }[] {
		const errorCounts: Record<string, number> = {};

		Object.entries(validationErrors).forEach(([key, error]) => {
			if (error) {
				// Parse the error key using centralized helper
				const fieldType = parseValidationErrorKey(key);

				let errorType: string;
				if (fieldType && columnDisplayNames[fieldType]) {
					// Use the centralized display names dictionary
					errorType = columnDisplayNames[fieldType];
				} else if (fieldType) {
					// Fallback: format the field type nicely
					errorType = fieldType.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
				} else {
					// Malformed key (should not happen with standardized pattern)
					console.warn(`Unexpected validation error key format: ${key}`);
					errorType = "Unknown Field";
				}

				errorCounts[errorType] = (errorCounts[errorType] || 0) + 1;
			}
		});

		return Object.entries(errorCounts)
			.map(([field, count]) => ({ field, count }))
			.sort((a, b) => b.count - a.count);
	}

	function exportCSV() {
		try {
			// Define the CSV headers
			const headers = [
				columnDisplayNames.grower,
				columnDisplayNames.farm,
				columnDisplayNames.field,
				columnDisplayNames.isReadyToSample,
				columnDisplayNames.density,
				columnDisplayNames.depth,
				columnDisplayNames.testPackage,
				columnDisplayNames.frequencyOfMicros,
				columnDisplayNames.fieldPriority,
				columnDisplayNames.salesperson,
				columnDisplayNames.lastCrop,
				columnDisplayNames.nextCrop,
				columnDisplayNames.yieldGoal,
				columnDisplayNames.dateFieldReadyEstimated,
				columnDisplayNames.labSubmissionCode,
				columnDisplayNames.labInstructions,
				columnDisplayNames.growerPhoneNumber,
				columnDisplayNames.submissionNotes,
				columnDisplayNames.fieldId,
				columnDisplayNames.submitterName,
				columnDisplayNames.submitterEmail,
			];

			// Build CSV rows
			const csvRows = [headers.join(",")];

			for (const job of jobs) {
				const row = [
					job.importGrowerName || "",
					job.importFarmName || "",
					job.importFieldName || "",
					job.isReadyToSample ? "Yes" : "No",
					job.importDensity?.toString() || "",
					job.importDepth?.toString() || "",
					job.externalTestPackageId || "",
					job.externalFrequencyOfMicros?.toString() || "",
					job.fieldPriority || "Regular Turn (default)",
					// Get salesperson names from IDs
					(job.salespersonIds || [])
						.map((id) => props.salespersons?.find((sp) => sp.id === id)?.name || "")
						.filter((name) => name)
						.join("; "),
					job.lastCrop || "",
					job.nextCrop || "",
					job.yieldGoal || "",
					job.dateFieldReadyEstimated?.toLocaleDateString() || "",
					job.labSubmissionCode || "",
					job.labInstructions || "",
					job.growerPhoneNumber || "",
					job.submissionNotes || "",
					job.fieldId || "",
					job.submitterName || "",
					job.submitterEmail || "",
				];

				// Escape fields that contain commas, quotes, or newlines
				const escapedRow = row.map((field) => {
					if (field.includes(",") || field.includes('"') || field.includes("\n")) {
						return `"${field.replace(/"/g, '""')}"`;
					}
					return field;
				});

				csvRows.push(escapedRow.join(","));
			}

			// Create and download the CSV file
			const csv = csvRows.join("\n");
			const blob = new Blob([csv], { type: "text/csv" });
			const filename = `job_import_${new Date().toISOString().split("T")[0]}.csv`;
			FileDownloader.downloadBlob(blob, filename);

			toast.success(`Exported ${jobs.length} jobs to CSV`);
		} catch (error) {
			Oops.handleError(error, "ImportTable.exportCSV");
			toast.error("Failed to export CSV");
		}
	}

	async function createJobs() {
		props.setLoading("submit");

		// Then validate all required fields
		if (hasValidationErrors()) {
			toast.error("Fill out required fields");
			props.setLoading(false);
			return;
		}

		if (allJobsHaveNoFiles(jobs)) {
			if (!window.confirm("No geodata files attached. Proceed?")) {
				return;
			}
		}

		const creating = toast.loading(`Creating ${jobs.length} Jobs...`);

		jobs.forEach((job) => {
			if (!job.jobFlags.includes("Bulk Upload")) job.jobFlags.push("Bulk Upload");
			job.dealIds = job.dealIds[0] === "custom" ? [] : job.dealIds;
		});

		try {
			let source: JobFlags;
			switch (props.integration) {
				case "Proagrica":
					source = "Created Portal API Proagrica";
					break;
				case "Agrian":
					source = "Created Portal API Agrian";
					break;
				default:
					source = "Created Portal Bulk";
					break;
			}
			await API.Data.Jobs.create.many(jobs, source);
			toast.success(`Created ${jobs.length} Jobs! Please give 1-2 business days for jobs to show up in the dashboard.`);
			props.resetForm();
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.JobImportTable.createJobs");
		} finally {
			toast.dismiss(creating);
			props.setLoading(false);
		}
	}

	// ============= RETURN VIEW MODEL =============
	return {
		columnContext,
		jobs,
		validationErrors,
		setValidationErrors,
		updateBoundaries,
		hasValidationErrors,
		getValidationErrorSummary,
		exportCSV,
		createJobs,
	};
}
